'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useConstant, usePrevious } from './internal/hooks';
import { SessionContext, SessionContextType } from './SessionContext';
import { Session } from './types';

import { useTRPC } from '../main-service-client';

export type SessionProviderProps = {
  children: ReactNode;
};

/* WARN
 * This provider is more complicated than it seems.
 * Talk to @riva and @dicey who racked their brain on this 😅
 * Any change should validate:
 * - having multiple tabs open
 * - login
 * - logout => should propagate to the other tab
 * - page reload while logged out AND logged in
 *
 * Fixed issues to be careful about not re-introducing:
 * - no context change on reload of a logged-out page: should see undefined => null
 * - race conditions between session / isLoading / sessionResolved (ex. sessionResolved before session appears in the context)
 * - "rebound" on logout: session => null (fromLoginLogout) => session (from stale useQuery.ping) => null (useQuery.ping catching up with the logout)
 *   due to the "ping" session competing with the "login/logout" session
 *   Occasionally causing "failed to log out" toast :/
 * - slowness to log out if "shouldPing" is not being updated fast enough on logout()
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const previousSession = usePrevious(session);
  const [shouldPing, setShouldPing] = useState(
    // very important: this MUST
    // - be true initially: this is the place that fetches the initial login/logout state
    // - stay true until the initial state is resolved (isQueryLoading)
    true
  );

  const { promise: sessionResolved, resolver } = useConstant(() => {
    let resolver: (() => void) | null = null;
    const promise = new Promise<void>((resolve) => {
      resolver = resolve;
    });
    return { promise, resolver: resolver! };
  });

  const { data: pingData, isLoading: isPingDataLoading } = useQuery(
    // Note: this refreshing query:
    // - loads the initial state
    // - catches logout from another tab
    // - ⚠️ lags behind after onLogin/onLogout
    trpc.user.ping.queryOptions(undefined, {
      staleTime: 60 * 1000, // 1 minute
      refetchInterval: shouldPing ? 15 * 1000 : false, // 15 seconds
      refetchOnWindowFocus: true,
      enabled: shouldPing,
    })
  );
  const sessionFromPing = pingData?.session;

  // useQuery.isLoading is true until the first ping succeeds,
  // then stays false forever
  // unless the query cache is cleared/removed while shouldPing is true
  const isPendingInitialResolution = session === undefined && isPingDataLoading;

  // this useEffect MUST be the SOLE owner of setSession() to avoid conflicts
  useEffect(() => {
    const targetSession: Session | null | undefined = sessionFromPing;

    const hasChange =
      targetSession?.sessionId !== previousSession?.sessionId || // filters out ref-only changes for the same session
      !previousSession; // to also detect the initial undef => null (other cases are covered by the previous check)

    if (isPendingInitialResolution) {
      // we're still loading the initial state:
      // - no change to apply since everything still has their initial value
      // - keep shouldPing to its initial value (true) so that the query keeps trying
      return;
    }

    if (hasChange) {
      setSession(targetSession);
      setShouldPing(!!targetSession);
    }
  }, [isPendingInitialResolution, sessionFromPing, previousSession]);

  useEffect(() => {
    if (session !== undefined) resolver();
  }, [session, resolver]);

  const onLogin = useCallback(
    (newSession: Session) => {
      // after a login, there is a window where sessionFromPing would be stale (still null)
      // => override to the new value
      queryClient.setQueryData(trpc.user.ping.queryKey(), {
        session: newSession,
        workerVersion: null,
        sessionKey: null,
      });
      setShouldPing(true); // important: helps the query session to catch up faster with sessionFromLoginLogout
    },
    [queryClient, trpc.user.ping]
  );

  const onLogout = useCallback(() => {
    // after a logout, there is a window where sessionFromPing would be stale (still the old session)
    // => override to the new value
    queryClient.setQueryData(trpc.user.ping.queryKey(), {
      session: null,
      workerVersion: null,
      sessionKey: null,
    });
    setShouldPing(false); // important: prevents the query session from refetching a stale session
  }, [queryClient, trpc.user.ping]);

  const value: SessionContextType = useMemo(() => {
    // Note: we can't use .isLoading from useQuery():
    // due to useEffect(), there is a window where useQuery() is no longer loading, but 'session' is still undefined
    const isLoading = session === undefined;

    const value: SessionContextType = {
      session,
      isLoading,
      sessionResolved,
      isLoggedIn: isLoading ? undefined : !!session,
      sessionKey: pingData?.sessionKey ?? null,
      workerVersion: pingData?.workerVersion ?? null,
      onLogin,
      onLogout,
    };

    return value;
  }, [
    session,
    pingData?.sessionKey,
    pingData?.workerVersion,
    onLogin,
    onLogout,
    sessionResolved,
  ]);

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
